import {useParams} from "react-router";
import * as React from "react";
import {gql} from "@apollo/client";
import {mutate, query} from "../../lib/apollo";
import Loading from "../../components/loading";
import useDebounce from "../../lib/UseDebounce";
import {
    Alert,
    Box,
    Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    FormControl,
    FormGroup, Grid, IconButton,
    Input,
    InputAdornment, InputLabel,
    Link, Menu, MenuItem,
    Paper, Select, Table, TableBody,
    TableCell,
    TableContainer, TableHead
} from "@mui/material";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TableHeaderRow from "../../components/TableHeaderRow";
import StripedTableRow from "../../components/StripedTableRow";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import ClickWallSchoolUpsert from "./clickWallSchoolUpsert";
import ClickWallProgramUpsert from "./clickWallProgramUpsert";
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import {toast} from "react-toastify";
import TextField from "@mui/material/TextField";
import {useEffect} from "react";

export default function ClickWallProgramsPage() {
    let { school_id } = useParams();
    const [loading, setLoading] = React.useState(true);
    const [search, setSearch] = React.useState('');
    const [school, setSchool] = React.useState(null as any);
    const [programs, setPrograms] = React.useState([] as any[]);
    const [programUpsert, setProgramUpsert] = React.useState(null as any);
    const [anchorEls, setAnchorEls] = React.useState<any>({});
    const [campaign, setCampaign] = React.useState("0");
    const [campaigns, setCampaigns] = React.useState([] as any[]);
    const [open, setOpen] = React.useState(false);
    const [programId, setProgramId] = React.useState(0);

    const getData = async (sSearch: string) => {
        const q = gql`
            query q ($criteria: ClickWallProgramCriteria!, $criteria2: ClickWallSchoolCriteria!, $criteria3: Int!) {
                clickWallPrograms(criteria: $criteria) {
                    id
                    name
                    description
                    is_active
                }
                clickWallSchools(criteria: $criteria2) {
                    id
                    name
                }
                clickWallHPCampaigns(schoolId: $criteria3) {
                    campaign {
                        id
                        name
                    }
                    programIds
                    allPrograms
                }
            }`;

        const variables = {
            criteria: {
                school_id: parseInt(school_id || '0'),
                search: sSearch
            },
            criteria2: {
                id: parseInt(school_id || '0')
            },
            criteria3: parseInt(school_id || '0')
        };

        return await query(q, variables);
    }

    const mapProgram = async (hp_campaign_id: number, program_id: number, action: "ADD" | "REMOVE") => {
        if (hp_campaign_id === 0 && action === "ADD") {
            setProgramId(program_id);
            setOpen(true);
            return;
        }

        let q;
        let variables;
        let good = false;


        variables = {
            hp_campaign_id: hp_campaign_id,
            program_id: program_id
        };


        setLoading(true);

        if (action === "ADD") {
            q = gql`
                mutation m($hp_campaign_id: Int!,$program_id: Int!) {
                    clickWallHPCampaignAddToProgram(hp_campaign_id: $hp_campaign_id, program_id: $program_id)
                }
            `;

            const res = await mutate(q, variables);
            good = res && res.clickWallHPCampaignAddToProgram;
        } else {
            q = gql`
                mutation m($hp_campaign_id: Int!,$program_id: Int!) {
                    clickWallHPCampaignRemoveFromProgram(hp_campaign_id: $hp_campaign_id, program_id: $program_id)
                }
            `;

            const res = await mutate(q, variables);
            good = res && res.clickWallHPCampaignRemoveFromProgram;
        }

        if (good) {
            await reload(search);
        } else {
            setLoading(false);
        }
    }

    const reload = async (sSearch: string) => {
        setLoading(true);
        const data = await getData(sSearch);
        setSchool(data.clickWallSchools[0]);
        document.title = `Click Wall Programs: ${data.clickWallSchools[0].name} | DN`;

        // sort data.clickWallPrograms by name
        let p = JSON.parse(JSON.stringify(data.clickWallPrograms)).sort((a: any, b: any) => {
            return a.name.localeCompare(b.name);
        });

        setPrograms(p);

        let campaigns = JSON.parse(JSON.stringify(data.clickWallHPCampaigns));
        campaigns.unshift({campaign: {id: 0, name: 'None'}, programIds: [], allPrograms: false});
        setCampaigns(campaigns);
        setLoading(false);
    }

    useDebounce(() => {
            reload(search).then(x=>{});
        }, [search], 400
    );

    const elipses = (str: string, len: number) => {
        if (str.length > len) {
            return str.substring(0, len) + '...';
        }
        return str;
    }

    if (!school) {
        return <> </>;
    }

    const selectedCampaign = campaigns.find(c => c.campaign.id === parseInt(campaign));

    return (
        <>
            <h2>CW Programs: {school.name}
                <Button component={Link} variant="outlined" size="small" sx={{margin: '0 5px'}}
                        href={`/clickWallSchools`}>&larr; Schools</Button>
            </h2>
            <FormGroup row={true}>
                <FormControl variant="outlined" sx={{ m: 1, minWidth: 120 }} size="small">
                    <Input
                        id="search"
                        type="text"
                        spellCheck="false"
                        placeholder="Search"
                        value={search || ''}
                        onChange={(e: any) => setSearch(e.target.value)}
                        startAdornment={<InputAdornment position="start"><SearchIcon /></InputAdornment>}
                    />
                </FormControl>
                <FormControl sx={{ minWidth: 120 }} size="small">
                    <Button aria-label="Add" onClick={() => { setProgramUpsert({}) }} variant={'contained'} color={'warning'}>
                        <AddBoxOutlinedIcon />&nbsp;Create Program
                    </Button>
                </FormControl>
                <FormControl size={'small'} sx={{minWidth: 200, marginLeft: '10px'}}>
                    <InputLabel id='lcampaign'>HP Campaigns</InputLabel>
                    <Select
                        labelId="lcampaign"
                        id="campaign"
                        value={campaign}
                        onChange={(e) => setCampaign(e.target.value)}
                        label="HP Campaigns"
                    >
                        {campaigns.map((x: any) => <MenuItem key={x.campaign.id} value={x.campaign.id}>{x.campaign.name}</MenuItem>)}
                    </Select>
                </FormControl>
            </FormGroup>
            {loading ? <Loading/> : <>
                {!programs || programs.length === 0 ? <Alert severity="warning">No results found</Alert> :
                    <>
                        <Box sx={{margin: '10px 0', fontWeight: 'bold', fontSize: '13px', display:"flex",}}>
                            Showing 1 to {programs.length.toLocaleString(undefined, {maximumFractionDigits: 0})} of {programs.length.toLocaleString(undefined, {maximumFractionDigits: 0})}.
                        </Box>
                        <TableContainer component={Paper} sx={{maxWidth: 'calc(100vw - 10px)', maxHeight: 'calc(100vh - 300px)'}}>
                            <Table size="small">
                                <TableHead>
                                    <TableHeaderRow>
                                        <TableCell>
                                            #
                                        </TableCell>
                                        <TableCell>
                                            Id
                                        </TableCell>
                                        <TableCell>
                                            Name
                                        </TableCell>
                                        <TableCell>
                                            Active
                                        </TableCell>
                                        <TableCell>
                                            Description
                                        </TableCell>
                                        <TableCell>

                                        </TableCell>
                                    </TableHeaderRow>
                                </TableHead>
                                <TableBody>
                                    {programs.map((row: any, index: number) => (
                                        <React.Fragment key={row.id}>
                                            <StripedTableRow>
                                                <TableCell>
                                                    <b>{index + 1}.</b>
                                                </TableCell>
                                                <TableCell>
                                                    {row.id}
                                                </TableCell>
                                                <TableCell style={{backgroundColor: getColor(selectedCampaign, row.id)}}>
                                                    <Link href={`/clickWallProgramMaps/${school_id}/${row.id}`}>{row.name}</Link>
                                                </TableCell>
                                                <TableCell>
                                                    {row.is_active && <CheckCircleOutlineIcon sx={{color: 'green', fontSize: '20px', marginLeft: '5px', verticalAlign: 'text-top'}}/>}
                                                    {!row.is_active && <HighlightOffIcon sx={{color: 'red', fontSize: '20px', marginLeft: '5px', verticalAlign: 'text-top'}}/>}
                                                </TableCell>
                                                <TableCell>
                                                    {elipses(row.description, 100)}
                                                </TableCell>


                                                <TableCell align="right">
                                                    <IconButton
                                                        aria-haspopup="true"
                                                        onClick={(event) => {
                                                            setAnchorEls({...anchorEls, [row.id]: event.currentTarget});
                                                        }}
                                                    >
                                                        <MoreVertIcon sx={{fontSize: '15px'}} />
                                                    </IconButton>
                                                    <Menu
                                                        anchorEl={anchorEls[row.id]}
                                                        open={!!anchorEls[row.id]}
                                                        onClose={() => {
                                                            setAnchorEls({...anchorEls, [row.id]: null});
                                                        }}
                                                    >
                                                        <MenuItem onClick={() => {
                                                            setProgramUpsert(row);
                                                            setAnchorEls({...anchorEls, [row.id]: null});
                                                        }} disableRipple><EditIcon sx={{marginRight: 1}} /> Edit Program</MenuItem>

                                                        {canAdd(selectedCampaign, row.id) && <MenuItem onClick={() => {
                                                            mapProgram(selectedCampaign.campaign.id, row.id, "ADD").then(() => {});
                                                            setAnchorEls({...anchorEls, [row.id]: null});
                                                        }} disableRipple><AddBoxOutlinedIcon sx={{marginRight: 1}} /> Add to HP</MenuItem>}

                                                        {canRemove(selectedCampaign, row.id) && <MenuItem onClick={() => {
                                                            mapProgram(selectedCampaign.campaign.id, row.id, "REMOVE").then(() => {});
                                                            setAnchorEls({...anchorEls, [row.id]: null});
                                                        }} disableRipple><DeleteOutlineOutlinedIcon sx={{marginRight: 1}} /> Remove from HP</MenuItem>}

                                                    </Menu>
                                                </TableCell>
                                            </StripedTableRow>
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </>}
            </>}

            <ClickWallProgramUpsert program={programUpsert} setProgram={setProgramUpsert} schoolId={parseFloat(school_id || '0')} reload={() => {reload(search).then(() => {})}} />
            <Add open={open} setOpen={setOpen} programId={programId} mapProgram={mapProgram} />
        </>
    );
}

function getColor(campaign: any, id: number) {
    if (campaign && (campaign.allPrograms || campaign.programIds.includes(id))) {
        return 'lightgreen';
    }
    return 'inherit';
}

function canAdd(campaign: any, id: number) {
    if (campaign.campaign && (campaign.allPrograms || !campaign.programIds.includes(id))) {
        return true;
    }
    return false;
}

function canRemove(campaign: any, id: number) {
    if (campaign && campaign.programIds.includes(id)) {
        return true;
    }
    return false;
}

function Add({open, setOpen, programId, mapProgram} : {open: any, setOpen: any, programId: number, mapProgram: any}) {
    const [addId, setAddId] = React.useState<string>('');

    useEffect(() => {
        setAddId("");
    }, []);

    const addCampaign = async () => {
        if (addId && !isNaN(Number(addId))) {
            setOpen(null);
            await mapProgram(parseInt(addId), programId, "ADD");
        }
    }

    if (!open) {
        return <></>
    }

    return (
        <Dialog
            open={!!open}
            onClose={() => setOpen(null)}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogTitle id="alert-dialog-title">
                {"Add HP Campaign to Program"}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-description">
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                autoComplete={'off'}
                                margin="dense"
                                id="hp_campaign_id"
                                label="HP Campaign Id to Add"
                                type="text"
                                fullWidth
                                size={'small'}
                                value={addId}
                                onChange={(e) => setAddId(e.target.value)}
                            />
                        </Grid>
                    </Grid>
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button sx={{color: 'red'}} onClick={() => setOpen(null)}>Close</Button>
                <Button sx={{color: 'green'}} onClick={() => addCampaign()}>Add</Button>
            </DialogActions>
        </Dialog>
    );
}
